/**
 * The deeper search on the case page (round 2, 13 Oct 2026): what it proposes, why, and
 * the two answers a person can give.
 *
 * - `InvestigationTrail`: the body of the "Why" panel. The fixed reason of the proposal,
 *   the doubt and the question above the lookups, then each lookup with its label of
 *   record, its query and the labels of its rows. It shows no row id, no token count and
 *   no words of the model: the server sends none of them.
 * - `ProposalWhy`: the "Why" button that opens the trail in a popover (the customer line).
 * - `ProposalActions`: "Confirm" and "Not this one". Hidden for a parked case, a case that
 *   is not open and while I2Q reads the case again (`canActOnProposals`). A proposal with
 *   a doubt gets an outline button, never the solid one-click button.
 * - `LineProposals`: the proposals of one line behind its chevron, the alternative last.
 * - `ProvisionalBlock`: the provisional values of a line with their sources.
 * - `InvestigationBar`: "Look again" for the case and the state while the request waits.
 *
 * Components come from `@norican/react` only. Every word is a label of record
 * (`lib/sales-flow.ts`, `lib/work-labels.ts`); the icons are in `lib/line-icons.ts`.
 */

import { Check, Icon, X } from "@norican/icons";
import { Badge, Button, Popover, Spinner, Typography } from "@norican/react";
import { useState } from "react";

import {
  canActOnProposals,
  canLookAgain,
  holds,
  lineProposalsOf,
  proposalIsRowItem,
} from "@/features/case-review/investigation";
import { type CaseWrites } from "@/features/case-review/use-case-writes";
import { INVESTIGATION_ICON } from "@/lib/line-icons";
import { logger } from "@/lib/logger";
import {
  type InvestigationProposal,
  type LineProvisional,
  type QuoteCaseDetail,
  type QuoteLine,
} from "@/lib/mock";
import { ACTION_LABELS } from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import {
  formatAmount,
  INVESTIGATION_WORDS,
  lookupLabel,
  PROVISIONAL_QTY_ASSUMED,
  provisionalDiscountText,
  provisionalLastPriceText,
  provisionalLeadText,
  provisionalPriceSentence,
  signalLabel,
} from "@/lib/work-labels";

/** The tag on a proposed account or item, with the icon of the deeper search. */
export function ProposalTag({ proposal }: { proposal: Pick<InvestigationProposal, "role"> }) {
  const alternative = proposal.role === "alternative";
  return (
    <Badge tone={alternative ? "neutral" : "info"} variant="outline" size="xs">
      <span className="inline-flex items-center gap-1">
        <Icon
          icon={alternative ? INVESTIGATION_ICON.alternative : INVESTIGATION_ICON.proposal}
          size="xs"
        />
        {alternative ? INVESTIGATION_WORDS.alternative : INVESTIGATION_WORDS.proposed}
      </span>
    </Badge>
  );
}

/** The doubt, the question, the reason and the lookups of one proposal. */
export function InvestigationTrail({ proposal }: { proposal: InvestigationProposal }) {
  const signals = proposal.signals
    .map((signal) => signalLabel(signal))
    .filter((label): label is string => label !== null);
  return (
    <div className="flex min-w-0 flex-col gap-2" data-investigation-trail={proposal.id}>
      {proposal.doubt && (
        <div className="flex flex-col gap-0.5" data-proposal-doubt>
          <Typography variant="caption" className="text-warning-fg font-semibold">
            {INVESTIGATION_WORDS.doubtLead}
          </Typography>
          <Typography variant="body-sm" className="text-fg break-words">
            {proposal.doubt}
          </Typography>
        </div>
      )}
      {proposal.question && (
        <Typography variant="body-sm" className="text-fg break-words" data-proposal-question>
          {proposal.question}
        </Typography>
      )}
      {proposal.reason && (
        <Typography variant="body-sm" tone="muted" className="break-words">
          {proposal.reason}
        </Typography>
      )}
      {signals.length > 0 && (
        <span className="flex flex-wrap gap-1">
          {signals.map((label) => (
            <Badge key={label} tone="neutral" variant="outline" size="xs">
              {label}
            </Badge>
          ))}
        </span>
      )}
      {proposal.evidence.length > 0 && (
        <div className="flex flex-col gap-1">
          <Typography variant="caption" tone="subtle">
            {INVESTIGATION_WORDS.lookups}
          </Typography>
          <ul className="flex flex-col gap-1.5">
            {proposal.evidence.map((step, index) => (
              <li
                key={index}
                className="border-border-subtle flex flex-col gap-0.5 border-b pb-1.5 text-xs last:border-b-0 last:pb-0"
                data-lookup={step.lookup}
              >
                <span className="flex flex-wrap items-center gap-1">
                  <span className="text-fg font-semibold">{lookupLabel(step.lookup)}</span>
                  {step.query && (
                    <span className="text-fg-muted font-mono break-all">{step.query}</span>
                  )}
                  {step.decided && (
                    <Badge tone="success" variant="subtle" size="xs">
                      {INVESTIGATION_WORDS.decided}
                    </Badge>
                  )}
                </span>
                {step.rows.length > 0 ? (
                  <ul className="flex flex-col">
                    {step.rows.map((row, rowIndex) => (
                      <li key={rowIndex} className="text-fg-muted break-words">
                        {row}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-fg-subtle">{INVESTIGATION_WORDS.noRows}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** "Why" on a proposal: the trail in a popover. */
export function ProposalWhy({
  proposal,
  caseId,
}: {
  proposal: InvestigationProposal;
  caseId: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        logger.debug("[case] proposal why", { case: caseId, proposal: proposal.id, open: next });
        if (next) {
          track("case.why_open", { what: "proposal" }, { caseId });
        }
        setOpen(next);
      }}
    >
      <Popover.Trigger
        render={
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            aria-label={INVESTIGATION_WORDS.whyHeading}
            data-proposal-why={proposal.id}
          />
        }
      >
        {ACTION_LABELS.why}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="start" sideOffset={6}>
          <Popover.Popup className="max-w-sm">
            <Popover.Header>
              <Popover.Title>
                <Typography as="span" variant="label" className="text-fg font-bold">
                  {INVESTIGATION_WORDS.whyHeading}
                </Typography>
              </Popover.Title>
              <Popover.CloseButton />
            </Popover.Header>
            <Popover.Body>
              <InvestigationTrail proposal={proposal} />
            </Popover.Body>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * "Confirm" and "Not this one" for one proposal, or what a person already answered.
 * Renders nothing when nobody may act (a parked case, a closed case, I2Q reading again).
 */
export function ProposalActions({
  detail,
  proposal,
  writes,
  onConfirm,
  confirmLabel = ACTION_LABELS.confirmLine,
  showConfirm = true,
}: {
  detail: QuoteCaseDetail;
  proposal: InvestigationProposal;
  writes: CaseWrites;
  /** The confirmation of today: the customer confirm, or the line decision. */
  onConfirm: () => void;
  confirmLabel?: string;
  /** False when the row or the line already carries the Confirm button for this proposal. */
  showConfirm?: boolean;
}) {
  if (proposal.decision) {
    return (
      <Typography variant="caption" tone="muted" data-proposal-decision={proposal.decision}>
        {proposal.decision === "confirmed"
          ? INVESTIGATION_WORDS.confirmed
          : INVESTIGATION_WORDS.rejected}
      </Typography>
    );
  }
  if (!canActOnProposals(detail)) {
    return null;
  }
  const named = proposal.name || proposal.account || proposal.item || "";
  return (
    <span className="flex flex-wrap items-center gap-1" data-proposal-actions={proposal.id}>
      {showConfirm && (
        <Button
          size="sm"
          // Never the solid one-click button: a person reads the reason first, and a
          // proposal with a doubt is a candidate only.
          variant="outline"
          tone={holds(proposal) ? "accent" : "neutral"}
          loading={writes.busy}
          startIcon={<Icon icon={Check} size="xs" />}
          aria-label={`Confirm the proposal ${named}`}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        tone="neutral"
        disabled={writes.busy}
        startIcon={<Icon icon={X} size="xs" />}
        aria-label={`Reject the proposal ${named}`}
        onClick={() => writes.rejectProposal(proposal.id)}
      >
        {ACTION_LABELS.notThisOne}
      </Button>
    </span>
  );
}

/**
 * The proposals of one line, behind its chevron: the tag, the item, the trail and the two
 * buttons. A proposal that is the item of the row is confirmed by the row's own
 * "Confirm"; an alternative and a candidate get their own button, which picks the item.
 */
export function LineProposals({
  detail,
  line,
  lineIndex,
  writes,
}: {
  detail: QuoteCaseDetail;
  line: QuoteLine;
  lineIndex: number;
  writes: CaseWrites;
}) {
  const proposals = lineProposalsOf(detail.investigation, line.lineKey);
  if (proposals.length === 0) {
    return null;
  }
  // A line a person decided keeps its proposals for the record, without buttons.
  const decided = Boolean(line.decision) || lineIndex < 0;
  return (
    <ul className="flex flex-col gap-2" data-line-proposals={line.id}>
      {proposals.map((proposal) => {
        const rowItem = proposalIsRowItem(proposal, line);
        return (
          <li
            key={proposal.id}
            className="border-border-subtle flex flex-col gap-1.5 rounded border p-2"
            data-proposal={proposal.id}
            data-proposal-status={proposal.status}
            data-proposal-role={proposal.role}
          >
            <span className="flex flex-wrap items-center gap-1.5 text-xs">
              <ProposalTag proposal={proposal} />
              <span className="text-fg font-mono font-semibold">{proposal.item}</span>
              {proposal.name && <span className="text-fg-muted break-words">{proposal.name}</span>}
            </span>
            {proposal.role === "alternative" && (
              <Typography variant="caption" tone="muted">
                {INVESTIGATION_WORDS.alternativeNote}
              </Typography>
            )}
            <InvestigationTrail proposal={proposal} />
            {(!decided || proposal.decision) && (
              <ProposalActions
                detail={detail}
                proposal={proposal}
                writes={writes}
                showConfirm={!rowItem}
                confirmLabel={proposal.role === "alternative" ? ACTION_LABELS.pickItem : undefined}
                onConfirm={() =>
                  writes.decideLine(lineIndex, line.lineKey ?? "", {
                    kind: "item",
                    item: proposal.item ?? "",
                  })
                }
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The item shown beside the item of a row: "Alternative 0990002 Blade". Changes nothing. */
export function AlternativeNote({
  detail,
  line,
}: {
  detail: Pick<QuoteCaseDetail, "investigation">;
  line: Pick<QuoteLine, "lineKey">;
}) {
  const alternative = lineProposalsOf(detail.investigation, line.lineKey).find(
    (proposal) => proposal.role === "alternative" && proposal.decision !== "rejected",
  );
  if (!alternative) {
    return null;
  }
  return (
    <span
      className="text-fg-muted inline-flex min-w-0 items-center gap-1 text-xs"
      title={INVESTIGATION_WORDS.alternativeNote}
      data-line-alternative={alternative.id}
    >
      <Icon icon={INVESTIGATION_ICON.alternative} size="xs" className="shrink-0" />
      {INVESTIGATION_WORDS.alternative}
      <span className="font-mono">{alternative.item}</span>
      {alternative.name && <span className="min-w-0 truncate">{alternative.name}</span>}
    </span>
  );
}

/** "41.50 GBP per 1 pcs": the figure of a provisional price; empty without one. */
export function provisionalFigure(provisional: LineProvisional | null | undefined): string {
  if (!provisional?.unitPrice) {
    return "";
  }
  const amount = [formatAmount(provisional.unitPrice), provisional.currency]
    .filter(Boolean)
    .join(" ");
  const per = [provisional.unitQty ?? 1, provisional.uom].filter(
    (part) => part != null && part !== "",
  );
  return `${amount} per ${per.join(" ")}`;
}

/** The chip of a provisional value. */
export function ProvisionalChip() {
  return (
    <Badge tone="neutral" variant="outline" size="xs" data-provisional-chip>
      {INVESTIGATION_WORDS.provisional}
    </Badge>
  );
}

/**
 * The provisional values of a line behind its chevron: the list price with its account
 * and price group, then the discount, the last quoted price and the lead time, each with
 * its source. None of them is a price to quote or a promise to the customer.
 */
export function ProvisionalBlock({
  provisional,
}: {
  provisional: LineProvisional | null | undefined;
}) {
  if (!provisional) {
    return null;
  }
  const figure = provisionalFigure(provisional);
  const rows = [
    provisionalDiscountText(provisional.discountPct, provisional.discountBasis),
    provisionalLastPriceText(
      provisional.lastUnitPrice,
      provisional.currency,
      provisional.lastQuotedOn,
    ),
    provisionalLeadText(provisional.leadDays, provisional.leadSource),
  ].filter((row): row is string => row !== null);
  return (
    <span className="flex flex-col gap-0.5 text-xs" data-provisional={provisional.account}>
      <span className="flex flex-wrap items-center gap-1">
        <ProvisionalChip />
        {figure && <span className="text-fg tabular-nums">{figure}</span>}
      </span>
      {figure && (
        <span className="text-fg-muted break-words">
          {provisionalPriceSentence(provisional.account, provisional.group)}
        </span>
      )}
      {figure && provisional.qtyAssumed && (
        <span className="text-fg-muted break-words">{PROVISIONAL_QTY_ASSUMED}</span>
      )}
      {rows.map((row) => (
        <span key={row} className="text-fg-muted break-words">
          {row}
        </span>
      ))}
    </span>
  );
}

/**
 * Over the draft lines: "Look again" for the case, the state while a request waits for
 * I2Q, and the sentence of a revised quotation. Renders nothing when none applies.
 */
export function InvestigationBar({
  detail,
  writes,
}: {
  detail: QuoteCaseDetail;
  writes: CaseWrites;
}) {
  const investigation = detail.investigation ?? null;
  if (!investigation) {
    return null;
  }
  const offered = canLookAgain(detail);
  const revision = investigation.revision;
  if (!investigation.pending && !offered && !revision) {
    return null;
  }
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-investigation-bar={investigation.pending ? "pending" : "idle"}
    >
      {investigation.pending ? (
        <span className="inline-flex items-center gap-2">
          {/* The DS Spinner announces itself (role "status" with this label). */}
          <Spinner size="sm" label={INVESTIGATION_WORDS.lookingAgain} />
          <Badge tone="info" variant="subtle" size="sm">
            {INVESTIGATION_WORDS.lookingAgain}
          </Badge>
          <Typography as="span" variant="caption" tone="muted">
            {INVESTIGATION_WORDS.lookingAgainNote}
          </Typography>
        </span>
      ) : (
        offered && (
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            loading={writes.busy}
            startIcon={<Icon icon={INVESTIGATION_ICON.lookAgain} size="xs" />}
            onClick={() => writes.lookAgain()}
            data-look-again="case"
          >
            {ACTION_LABELS.lookAgain}
          </Button>
        )
      )}
      {revision && (
        <Typography as="span" variant="caption" tone="muted" data-revision={revision.quotation}>
          {INVESTIGATION_WORDS.revision(revision.quotation, revision.kind)}
        </Typography>
      )}
    </div>
  );
}
